'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { useState } from 'react';
import type { ImageType } from '@eliadmin/core/src/api/api-types';
import type { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';
import type { Swiper as SwiperType } from 'swiper';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { HtmlDescription } from '../shared/html-description';
import { PreviewVideo } from '../shared/preview-video';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { CgArrowLeft, CgArrowRight } from 'react-icons/cg';
import { Button } from '../ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { STUDIO_EVENT_SLUG } from '../../constants/studio';

const STUDIO_PROMO: Record<string, { title: string; about: string; reservations: string }> = {
  sk: {
    title: 'Navštívte naše Štúdio Nobelova',
    about: 'O štúdiu',
    reservations: 'Rezervácie',
  },
  en: {
    title: 'Visit our Studio Nobelova',
    about: 'About the studio',
    reservations: 'Reservations',
  },
};

interface HeroProps {
  slides: TDynamicData[];
  title: string;
  description: string;
  locale: string;
  studioReservationUrl?: string;
}

export function Hero({ slides, title, description, locale, studioReservationUrl }: HeroProps) {
  const { t } = useTranslations();
  const studioPromo = STUDIO_PROMO[locale] ?? STUDIO_PROMO.sk;
  const studioHref = `/${STUDIO_EVENT_SLUG}`;
  const [swiper, setSwiper] = useState<SwiperType | null>(null);

  return (
    <>
      <section
        className={cn(
          'custom-section',
          'border-b border-b-white/20 bg-black py-12 pr-0 sm:py-14 md:pr-6',
        )}
      >
        <div
          className={cn(
            'custom-container',
            'flex flex-col items-center justify-start gap-12 md:flex-row md:justify-between md:gap-6 lg:gap-12 xl:gap-16',
          )}
        >
          <div className="flex w-full flex-col items-start justify-start gap-6 pr-6 md:max-w-[505px] md:pr-0 lg:gap-8">
            <h2 className="max-w-[300px] font-heading text-[3.3rem] font-normal leading-none text-white sm:text-[4.125rem] lg:text-[4.95rem]">
              {title}
            </h2>
            <HtmlDescription
              content={description}
              className="ea-hero__description w-full text-white/70"
              size="base"
            />
            <div className="flex w-full items-center justify-start gap-8">
              <Link
                prefetch={false}
                prefetchOnHover
                href={'#online-classes'}
                className="text-lg font-normal text-primary sm:text-xl xl:text-2xl"
              >
                Online classes
              </Link>
              <Link
                prefetch={false}
                prefetchOnHover
                href={studioHref}
                className="text-lg font-normal text-primary sm:text-xl xl:text-2xl"
              >
                Studio
              </Link>
              <Link
                prefetch={false}
                prefetchOnHover
                href={ROUTES.EVENTS()}
                className="text-lg font-normal text-primary sm:text-xl xl:text-2xl"
              >
                Events
              </Link>
            </div>
            <div className="flex w-full flex-col items-start justify-start gap-4 bg-primary p-4 lg:gap-6 lg:p-6">
              <p className="font-heading text-xl font-medium text-white lg:text-2xl xl:text-3xl">
                {studioPromo.title}
              </p>
              <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
                <Button
                  asChild
                  variant={'secondary'}
                  className="col-span-1 w-full whitespace-nowrap"
                >
                  <Link prefetch={false} prefetchOnHover href={studioHref}>
                    {studioPromo.about}
                  </Link>
                </Button>
                <Button asChild variant={'dark'} className="col-span-1 w-full whitespace-nowrap">
                  {studioReservationUrl ? (
                    <Link
                      prefetch={false}
                      href={studioReservationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {studioPromo.reservations}
                    </Link>
                  ) : (
                    <Link prefetch={false} prefetchOnHover href={studioHref}>
                      {studioPromo.reservations}
                    </Link>
                  )}
                </Button>
              </div>
            </div>
          </div>
          <div className="flex w-full flex-col items-end justify-end gap-4 md:max-w-[670px]">
            <Swiper
              slidesPerView={'auto'}
              loop={true}
              navigation={{
                prevEl: '.swiper-button-prev',
                nextEl: '.swiper-button-next',
              }}
              modules={[Autoplay, Navigation]}
              onSwiper={setSwiper}
              className={cn(
                // Slidy sú vždy 9:16. Šírka karuselu = 1,1 × šírka slidu + medzera, takže ďalší slide je vidieť na 10 %.
                'relative w-full self-start sm:max-w-[346px] md:self-end lg:max-w-[412px] xl:max-w-[456px]',
                'ea-hero-swiper__wrapper',
              )}
            >
              {slides.map(slide => (
                <SwiperSlide
                  key={slide.slug}
                  className={cn(
                    'relative aspect-[9/16] !h-auto !w-[calc((100%-16px)/1.1)]',
                  )}
                >
                  {({ isActive, isNext }) => (
                    <HeroSlide slide={slide} showFirstFrame={isActive || isNext} />
                  )}
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="mt-4 hidden items-center justify-end gap-4 pr-4 sm:flex sm:pr-0">
              <button
                type="button"
                aria-label={t('text3525')}
                onClick={() => swiper?.slidePrev()}
                className="h-6 w-6 cursor-pointer !text-white"
              >
                <CgArrowLeft size={24} />
              </button>
              <button
                type="button"
                aria-label={t('text3526')}
                onClick={() => swiper?.slideNext()}
                className="h-6 w-6 cursor-pointer !text-white"
              >
                <CgArrowRight size={24} />
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

interface HeroSlideProps {
  slide: TDynamicData;
  showFirstFrame: boolean;
}

function HeroSlide({ slide, showFirstFrame }: HeroSlideProps) {
  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const allImages = [slide?.image, ...(slide.fields[1]?.images ?? [])].filter(
    (image): image is ImageType => !!image?.url,
  );
  const videoUrl = slide?.fields[2]?.file?.url;

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  if (videoUrl) {
    return (
      <PreviewVideo src={videoUrl} poster={allImages[0]?.url}
        sizes="(min-width: 1280px) 400px, (min-width: 1024px) 360px, 300px"
        showFirstFrame={showFirstFrame}
        className="col-span-1 hover:opacity-90" overlayClassName="bg-black/30" />
    );
  }

  return (
    <>
      <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
      <button
        type="button"
        data-ea-visual-tag="div"
        aria-label={slide.name}
        onClick={handleOpenGallery(allImages, 0)}
        className={cn('relative h-full w-full', 'group transition-all')}
      >
        <Image
          src={allImages[0]?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(allImages?.[0], slide?.name)}
          width={0}
          height={0}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          sizes="100vw"
        />
      </button>
    </>
  );
}
